/** The note's name, at the head of page 1.
 *
 * People reach for the top-left of the first page to name a note — that is
 * where a title lives in every notebook they have ever used — and the app only
 * offered a chrome bar, so the reach failed and notes stayed "Untitled".
 *
 * It sits in the page's TOP MARGIN rather than printed on the sheet. Both of
 * the first two attempts put it on the paper, and both were wrong in the same
 * way: the paper is an ink surface, so anything on it either steals strokes or
 * has to refuse the pointer to avoid stealing them. The first version was a
 * full-width input that swallowed every stroke starting near the top of page 1.
 * The second only became a field with NO ink tool armed — which on a canvas
 * note is never, because opening one arms the pen, so the title could not be
 * clicked at all.
 *
 * In the margin there is no conflict to arbitrate. It is always a field, the
 * whole sheet stays inkable, and the title still reads as the heading of page
 * one. The page-number chip has always lived up here, and the page ACTIONS
 * moved below the sheet to leave this strip to the title.
 *
 * It is sized from the page width, not the viewport. `clamp(…, 2.6vw, …)` made
 * the title's size relative to the WINDOW, so its proportion to the paper
 * changed with the window and drifted against the zoom transform scaling the
 * page underneath it. One page-relative number scales exactly with the sheet.
 */
import * as React from "react";

import { cn } from "@/lib/utils";

/** Title size as a fraction of the page's width. A US-Letter sheet at 816px
 *  lands on ~31px, which is the size the first version peaked at. */
const TITLE_RATIO = 0.038;
/** The date beneath it, same basis. */
const STAMP_RATIO = 0.0145;

export function PageTitle({
  title,
  onRename,
  editable,
  createdAt,
  pageWidth,
}: {
  title: string;
  onRename: (next: string) => void;
  /** Whether the note can be renamed at all — the markup capability flag, not
   *  the ink mode. In the margin there is no reason to care which tool is up. */
  editable: boolean;
  createdAt?: string | null;
  /** The rendered page width in px. The title is a fraction of it. */
  pageWidth: number;
}) {
  const [draft, setDraft] = React.useState(title);
  const [editing, setEditing] = React.useState(false);

  // Follow the document while not typing, so a rename from the chrome bar or
  // another surface shows up here too.
  React.useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== title) onRename(next);
    else setDraft(title);
  };

  const stamp = React.useMemo(() => {
    const d = createdAt ? new Date(createdAt) : null;
    if (!d || Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [createdAt]);

  // `Untitled` is a PLACEHOLDER, never a stored value — a note you never named
  // should read as unnamed, not as a note called "Untitled".
  const placeholder = "Untitled note";
  const shown = title === "Untitled" || title === placeholder ? "" : title;

  const titlePx = Math.max(13, pageWidth * TITLE_RATIO);
  const stampPx = Math.max(8, pageWidth * STAMP_RATIO);

  return (
    <div
      className="absolute left-0 z-10 flex flex-col items-start"
      style={{ bottom: "100%", marginBottom: Math.max(4, pageWidth * 0.008), maxWidth: "72%" }}
    >
      {editable ? (
        <input
          value={editing ? draft : shown}
          placeholder={placeholder}
          aria-label="Note title"
          onFocus={() => {
            setEditing(true);
            setDraft(shown);
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            // The shell binds bare keys (Space opens Tools, [ and ] move the
            // panels). None of them may fire while a name is being typed.
            e.stopPropagation();
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === "Escape") {
              setDraft(shown);
              setEditing(false);
              (e.target as HTMLInputElement).blur();
            }
          }}
          size={Math.max(10, (editing ? draft : shown || placeholder).length)}
          className={cn(
            "max-w-full rounded-sm border-0 bg-transparent p-0 font-display font-semibold leading-tight",
            "tracking-tight text-foreground outline-none placeholder:text-muted-foreground/60",
            "transition-colors hover:bg-foreground/5 focus-visible:bg-foreground/5 focus-visible:ring-0",
          )}
          style={{ fontSize: titlePx }}
        />
      ) : (
        <div
          className="max-w-full truncate font-display font-semibold leading-tight tracking-tight text-foreground"
          style={{ fontSize: titlePx }}
        >
          {shown || <span className="text-muted-foreground/60">{placeholder}</span>}
        </div>
      )}
      {stamp && (
        <div
          className="text-muted-foreground/70"
          style={{ fontSize: stampPx, marginTop: stampPx * 0.3 }}
        >
          {stamp}
        </div>
      )}
    </div>
  );
}
